import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FiFilter } from "react-icons/fi";
import ControlsHeader from "shared/ControlsHeader";
import TabActionBar from "shared/TabActionBar";
import Button from "shared/Button";
import Pagination from "shared/Pagination";
import SearchBar from "shared/SearchBar";
import Loader from "shared/Loader";
import DeliverySectionHeader from "shared/DeliverySectionHeader";
import AudioListItem from "DreamCast/Shared/AudioListItem";
import SortModal from "DreamCast/Shared/SortModal";
import { FilterColumn } from "shared/FilterColumn";
import {
  fetchDreamcastDigitalDelivery,
  duplicateDreamcastDigitalDelivery,
  setDreamcastDigitalDeliveryStatus,
} from "DreamCast/api/digitalDelivery";
import { fetchDreamcastContentSettingsConfig } from "DreamCast/api/media";
import {
  buildTagOptionsByTypeCode,
  extractSettingsTagDefinitions,
} from "DreamCast/Shared/ContentSettingsCard/tagSchema";
import styles from "./DigitalDelivery.module.css";
import {
  deliveryWorkflowStatus,
  splitDeliveryItems,
} from "./deliveryBuckets";
import {
  deliveryYearOptions,
  matchesDeliveryDateFilters,
} from "./deliveryDateFilters";
import { matchesCategoryFilter } from "DreamCast/Shared/categoryFilter";

const FILTER_SECTIONS = [
  {
    label: "Type",
    options: [
      { label: "WWG", value: "wwg" },
      { label: "3rd Party", value: "3rd" },
    ],
  },
  {
    label: "Category",
    options: [
      { label: "Audio", value: "Audio" },
      { label: "Video", value: "Video" },
      { label: "Music", value: "Music" },
    ],
  },
  {
    label: "Language",
    options: [],
  },
  {
    label: "Tag",
    options: [],
  },
  {
    label: "Status",
    options: [
      { label: "Published", value: "published" },
      { label: "In Review", value: "in-review" },
      { label: "Archived", value: "archived" },
      { label: "Upcoming", value: "upcoming" },
    ],
  },
  {
    label: "Year",
    // Built from the records plus the current year (see deliveryYearOptions).
    options: [],
  },
  {
    label: "Month",
    options: [
      { label: "January", value: "01" },
      { label: "February", value: "02" },
      { label: "March", value: "03" },
      { label: "April", value: "04" },
      { label: "May", value: "05" },
      { label: "June", value: "06" },
      { label: "July", value: "07" },
      { label: "August", value: "08" },
      { label: "September", value: "09" },
      { label: "October", value: "10" },
      { label: "November", value: "11" },
      { label: "December", value: "12" },
    ],
  },
];

export function DigitalDelivery({
  onAddNew,
  onEdit,
  currentItems,
  upcomingItems,
  historyItems,
  disableInitialFetch,
}) {
  const safeCurrentItems = Array.isArray(currentItems) ? currentItems : [];
  const safeUpcomingItems = Array.isArray(upcomingItems) ? upcomingItems : [];
  const safeHistoryItems = Array.isArray(historyItems) ? historyItems : [];

  const [activeTab, setActiveTab] = useState("Current");
  const [showHistoryFilters, setShowHistoryFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date-newest");
  const [currentItemsState, setCurrentItemsState] = useState(safeCurrentItems);
  const [upcomingItemsState, setUpcomingItemsState] =
    useState(safeUpcomingItems);
  const [historyItemsState, setHistoryItemsState] = useState(safeHistoryItems);
  const [apiError, setApiError] = useState("");
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [duplicatingItemId, setDuplicatingItemId] = useState(null);
  const [editInProgressId, setEditInProgressId] = useState(null);
  const [archivingItemId, setArchivingItemId] = useState(null);
  const [contentSettingsConfig, setContentSettingsConfig] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    let isSubscribed = true;

    fetchDreamcastContentSettingsConfig()
      .then((response) => {
        if (!isSubscribed) {
          return;
        }

        const payload = response?.data || response;
        setContentSettingsConfig(payload || null);
      })
      .catch(() => {
        // Fall back to data-derived options when config is unavailable.
      });

    return () => {
      isSubscribed = false;
    };
  }, []);

  const loadItems = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const startTime = Date.now();
      const response = await fetchDreamcastDigitalDelivery();
      const items = Array.isArray(response?.data) ? response.data : [];
      // removed duplicate debug logging for typeGroup
      const { current, upcoming, history } = splitDeliveryItems(items);
      setCurrentItemsState(current);
      setUpcomingItemsState(upcoming);
      setHistoryItemsState(history);

      // removed archived history debug logging

      // Ensure minimum 500ms display time for loader visibility
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 500 - elapsed);
      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (disableInitialFetch) {
      setCurrentItemsState(safeCurrentItems);
      setUpcomingItemsState(safeUpcomingItems);
      setHistoryItemsState(safeHistoryItems);
      return;
    }

    loadItems().catch((err) =>
      setApiError(err.message || "Failed to load digital delivery items."),
    );
  }, [
    disableInitialFetch,
    loadItems,
    safeCurrentItems,
    safeUpcomingItems,
    safeHistoryItems,
  ]);

  const SORT_OPTIONS = [
    { label: "Date (Newest First)", value: "date-newest" },
    { label: "Date (Oldest First)", value: "date-oldest" },
    { label: "Title (A-Z)", value: "title-asc" },
    { label: "Title (Z-A)", value: "title-desc" },
  ];

  const handleSort = (value) => {
    setSortBy(value);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const filterSections = useMemo(() => {
    const allItems = [
      ...currentItemsState,
      ...upcomingItemsState,
      ...historyItemsState,
    ];

    const definitions = extractSettingsTagDefinitions(contentSettingsConfig);
    const optionsByTypeCode = buildTagOptionsByTypeCode(definitions);

    const languageSource = Array.isArray(optionsByTypeCode.LANGUAGE)
      ? optionsByTypeCode.LANGUAGE
      : allItems.map((item) => item?.language);

    const tagSource = Array.isArray(optionsByTypeCode.KEYWORD_TAG)
      ? optionsByTypeCode.KEYWORD_TAG
      : allItems.flatMap((item) =>
          Array.isArray(item?.tags) ? item.tags : [],
        );

    const languageOptions = Array.from(
      new Map(
        languageSource
          .map((value) => String(value || "").trim())
          .filter(Boolean)
          .map((label) => [
            label.toLowerCase(),
            { label, value: label.toLowerCase() },
          ]),
      ).values(),
    );

    const tagOptions = Array.from(
      new Map(
        tagSource
          .map((value) => String(value || "").trim())
          .filter(Boolean)
          .map((label) => [
            label.toLowerCase(),
            { label, value: label.toLowerCase() },
          ]),
      ).values(),
    );

    return FILTER_SECTIONS.map((section) => {
      if (section.label === "Language") {
        return {
          ...section,
          options: languageOptions,
        };
      }

      if (section.label === "Tag") {
        return {
          ...section,
          options: tagOptions,
        };
      }

      if (section.label === "Year") {
        return { ...section, options: deliveryYearOptions(allItems) };
      }

      return section;
    });
  }, [
    contentSettingsConfig,
    currentItemsState,
    historyItemsState,
    upcomingItemsState,
  ]);

  const filterItems = (items) => {
    return items.filter((item) => {
      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const tagsString = Array.isArray(item.tags) ? item.tags.join(" ") : "";
        const hiddenTagsString = Array.isArray(
          item.contentSettings?.tags?.hiddenTags,
        )
          ? item.contentSettings.tags.hiddenTags.join(" ")
          : "";
        const searchable = `${item.title || ""} ${item.contributorName || ""} ${
          item.sku || ""
        } ${tagsString} ${hiddenTagsString}`.toLowerCase();
        if (!searchable.includes(q)) return false;
      }

      // Type filter (compare against `typeGroup` which contains codes like 'wwg'/'3rd')
      if (filters.Type && filters.Type.length > 0) {
        const selectedTypes = filters.Type.map((v) =>
          String(v || "").toLowerCase(),
        );
        const itemTypeGroup = String(
          item.typeGroup || item.type || item.mediaTypeCode || "",
        ).toLowerCase();
        if (!selectedTypes.includes(itemTypeGroup)) return false;
      }

      // Category filter (Audio / Video / Music, combinable).
      if (!matchesCategoryFilter(item, filters.Category)) return false;

      // Language filter
      if (filters.Language && filters.Language.length > 0) {
        const normalizedLanguage = String(item.language || "").toLowerCase();
        const selectedLanguages = filters.Language.map((value) =>
          String(value || "").toLowerCase(),
        );
        if (!selectedLanguages.includes(normalizedLanguage)) return false;
      }

      // Tag filter
      if (filters.Tag && filters.Tag.length > 0) {
        const itemTags = (Array.isArray(item.tags) ? item.tags : []).map(
          (tag) => String(tag || "").toLowerCase(),
        );
        const hasMatchingTag = filters.Tag.some((tag) =>
          itemTags.includes(String(tag || "").toLowerCase()),
        );
        if (!hasMatchingTag) return false;
      }

      // Status filter
      if (filters.Status && filters.Status.length > 0) {
        if (!filters.Status.includes(deliveryWorkflowStatus(item))) return false;
      }

      // Year / Month filters
      if (!matchesDeliveryDateFilters(item, filters)) return false;

      return true;
    });
  };

  const sortItems = (items) => {
    const sorted = [...items];
    switch (sortBy) {
      case "date-newest": {
        return sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
      }
      case "date-oldest": {
        return sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
      }
      case "title-asc": {
        return sorted.sort((a, b) =>
          (a.title || "").localeCompare(b.title || ""),
        );
      }
      case "title-desc": {
        return sorted.sort((a, b) =>
          (b.title || "").localeCompare(a.title || ""),
        );
      }
      default:
        return sorted;
    }
  };

  // Pagination logic for current items
  const paginatedCurrentItems = useMemo(() => {
    const filtered = filterItems(currentItemsState);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [currentPage, currentItemsState, filters, searchQuery]);

  const currentTotalPages = Math.ceil(
    filterItems(currentItemsState).length / ITEMS_PER_PAGE,
  );

  // Pagination logic for upcoming items
  const paginatedUpcomingItems = useMemo(() => {
    const filtered = filterItems(upcomingItemsState);
    const startIndex = (upcomingPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [upcomingPage, upcomingItemsState, filters, searchQuery]);

  const upcomingTotalPages = Math.ceil(
    filterItems(upcomingItemsState).length / ITEMS_PER_PAGE,
  );

  const allItemsList = [
    ...currentItemsState,
    ...upcomingItemsState,
    ...historyItemsState,
  ];
  const totalFilteredCount = Array.isArray(allItemsList)
    ? filterItems(allItemsList).length
    : 0;

  const filtersHaveSelection = Object.keys(filters || {}).some(
    (key) => Array.isArray(filters[key]) && filters[key].length > 0,
  );

  const searchBarNoResults =
    totalFilteredCount === 0 && String(searchQuery || "").trim() !== "";

  // For the History tab specifically, detect when filters (not search) return no history items
  const historyFilteredCount = Array.isArray(historyItemsState)
    ? filterItems(historyItemsState).length
    : 0;
  const historyFiltersNoResults =
    historyFilteredCount === 0 && filtersHaveSelection;

  // Pagination logic for history items
  const paginatedHistoryItems = useMemo(() => {
    const filtered = sortItems(filterItems(historyItemsState));
    const startIndex = (historyPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [historyPage, historyItemsState, filters, searchQuery, sortBy]);

  const historyTotalPages = Math.ceil(
    sortItems(filterItems(historyItemsState)).length / ITEMS_PER_PAGE,
  );

  // Reset pages when filters change
  useEffect(() => {
    setCurrentPage(1);
    setUpcomingPage(1);
    setHistoryPage(1);
  }, [filters, searchQuery, sortBy]);

  const handleFilterChange = (nextFilters) => {
    setFilters(nextFilters);
  };

  const handleAddDigitalDelivery = () => {
    onAddNew();
  };

  const handleArchiveToggle = async (id) => {
    const all = [
      ...currentItemsState,
      ...upcomingItemsState,
      ...historyItemsState,
    ];
    const target = all.find((item) => item.mediaItemId === id);
    if (!target) return;

    const isArchived =
      String(target.status || target.workflowStatus || "").toLowerCase() ===
      "archived";
    const workflowStatus = isArchived ? "published" : "archived";
    const displayStatus = isArchived ? "Available" : "Archived";

    setArchivingItemId(id);
    try {
      setApiError("");
      await setDreamcastDigitalDeliveryStatus(
        id,
        workflowStatus,
        displayStatus,
      );
      await loadItems();
    } catch (error) {
      setApiError(error.message || "Failed to update status.");
    } finally {
      setArchivingItemId(null);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      setApiError("");
      const allItems = [
        ...currentItemsState,
        ...upcomingItemsState,
        ...historyItemsState,
      ];
      const sourceItem = allItems.find((item) => item.mediaItemId === id);
      if (!sourceItem) return;

      // Track the ORIGINAL item ID as duplicating
      setDuplicatingItemId(id);

      // Duplicate in the background
      await duplicateDreamcastDigitalDelivery(id);

      // Reload items to sync with server
      await loadItems();
    } catch (error) {
      setApiError(error.message || "Failed to duplicate item.");
      await loadItems();
    } finally {
      // Clear duplicate tracking after a brief delay to ensure UI updates
      setTimeout(() => {
        setDuplicatingItemId(null);
      }, 100);
    }
  };

  const handleEdit = async (id) => {
    if (editInProgressId != null && editInProgressId !== id) {
      return;
    }

    try {
      setApiError("");
      setEditInProgressId(id);
      await onEdit(id);
    } catch (error) {
      setApiError(error.message || "Failed to open edit view.");
    } finally {
      setEditInProgressId(null);
    }
  };

  return (
    <div className={styles.page}>
      <ControlsHeader title="DreamCast - Digital Delivery" />
      {apiError && <p className={styles.apiError}>{apiError}</p>}

      <TabActionBar
        tabs={[
          { label: "Current", value: "Current" },
          { label: "History", value: "History" },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
      />

      <div className={styles.toolbar}>
        <SearchBar
          placeholderText="Search Media"
          onInput={handleSearch}
          noResults={searchBarNoResults}
          minChar={0}
          searchDelay={0}
        />
        <Button onClick={handleAddDigitalDelivery}>Add Digital Delivery</Button>
        {activeTab === "History" && (
          <SortModal
            options={SORT_OPTIONS}
            selected={sortBy}
            onChange={handleSort}
          />
        )}
      </div>

      {isLoadingList &&
      currentItemsState.length === 0 &&
      upcomingItemsState.length === 0 &&
      historyItemsState.length === 0 ? (
        <div className={styles.initialLoaderOverlay}>
          <Loader color="#415364" size={85} />
        </div>
      ) : (
        <div className={styles.content}>
          {activeTab === "Current" ? (
            <>
              <div className={styles.columnHeaders}>
                <span className={styles.colImage}>Image</span>
                <div className={styles.headerInfoCluster}>
                  <span className={styles.colTitle}>Title/Info</span>
                  <div className={styles.headerRightColumn}>
                    <span className={styles.colSku}>SKU</span>
                    <span className={styles.colType}>Type</span>
                    <span className={styles.colStatus}>Status</span>
                  </div>
                </div>
              </div>
              <div className={styles.section}>
                <DeliverySectionHeader
                  label="Current Digital Delivery"
                  variant="current"
                />
                {paginatedCurrentItems.map((item) => {
                  const isEditLoading = editInProgressId === item.mediaItemId;
                  const isDuplicateLoading =
                    String(duplicatingItemId) === String(item.mediaItemId);
                  const isArchiveLoading = archivingItemId === item.mediaItemId;
                  const isItemLoading =
                    isEditLoading || isDuplicateLoading || isArchiveLoading;

                  const audioType = item.mediaTypeCode
                    ? item.mediaTypeCode.charAt(0).toUpperCase() +
                      item.mediaTypeCode.slice(1)
                    : "Audio";

                  return (
                    <AudioListItem
                      key={item.mediaItemId}
                      id={item.mediaItemId}
                      thumbnail={item.thumbnailPath}
                      title={item.title}
                      date={item.date}
                      speaker={item.contributorName}
                      episode={item.episode}
                      sku={item.sku}
                      type={audioType}
                      status={item.displayStatus || "In Review"}
                      isDraft={item.isDraft}
                      tags={item.tags || []}
                      pinPermissions={item.pinPermissions}
                      membershipPermissions={item.membershipPermissions}
                      onDuplicate={() => handleDuplicate(item.mediaItemId)}
                      onArchive={() => handleArchiveToggle(item.mediaItemId)}
                      onEdit={() => handleEdit(item.mediaItemId)}
                      disabled={isItemLoading}
                      isEditLoading={isEditLoading}
                      isDuplicateLoading={isDuplicateLoading}
                      isArchiveLoading={isArchiveLoading}
                    />
                  );
                })}

                {currentTotalPages > 1 && (
                  <div className={styles.paginationWrapper}>
                    <Pagination
                      page={currentPage}
                      totalPages={currentTotalPages}
                      onPageChange={setCurrentPage}
                      ariaLabel="Current delivery pagination"
                    />
                  </div>
                )}
              </div>

              <div className={styles.section}>
                <DeliverySectionHeader
                  label="Upcoming Digital Delivery"
                  variant="upcoming"
                />
                {paginatedUpcomingItems.map((item) => {
                  const isEditLoading = editInProgressId === item.mediaItemId;
                  const isDuplicateLoading =
                    String(duplicatingItemId) === String(item.mediaItemId);
                  const isArchiveLoading = archivingItemId === item.mediaItemId;
                  const isItemLoading =
                    isEditLoading || isDuplicateLoading || isArchiveLoading;

                  const audioType = item.mediaTypeCode
                    ? item.mediaTypeCode.charAt(0).toUpperCase() +
                      item.mediaTypeCode.slice(1)
                    : "Audio";

                  return (
                    <AudioListItem
                      key={item.mediaItemId}
                      id={item.mediaItemId}
                      thumbnail={item.thumbnailPath}
                      title={item.title}
                      date={item.date}
                      speaker={item.contributorName}
                      episode={item.episode}
                      sku={item.sku}
                      type={audioType}
                      status={item.displayStatus || "In Review"}
                      isDraft={item.isDraft}
                      tags={item.tags || []}
                      pinPermissions={item.pinPermissions}
                      membershipPermissions={item.membershipPermissions}
                      onDuplicate={() => handleDuplicate(item.mediaItemId)}
                      onArchive={() => handleArchiveToggle(item.mediaItemId)}
                      onEdit={() => handleEdit(item.mediaItemId)}
                      disabled={isItemLoading}
                      isEditLoading={isEditLoading}
                      isDuplicateLoading={isDuplicateLoading}
                      isArchiveLoading={isArchiveLoading}
                    />
                  );
                })}

                {upcomingTotalPages > 1 && (
                  <div className={styles.paginationWrapper}>
                    <Pagination
                      page={upcomingPage}
                      totalPages={upcomingTotalPages}
                      onPageChange={setUpcomingPage}
                      ariaLabel="Upcoming delivery pagination"
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className={styles.historyLayout}>
              <button
                type="button"
                className={styles.historyFilterToggle}
                aria-expanded={showHistoryFilters}
                aria-controls="digital-delivery-history-filters"
                onClick={() => setShowHistoryFilters((visible) => !visible)}
              >
                <FiFilter aria-hidden="true" />
                Filters
              </button>
              <div
                id="digital-delivery-history-filters"
                className={`${styles.historyFilters} ${
                  showHistoryFilters ? styles.historyFiltersOpen : ""
                }`}
              >
                <FilterColumn
                  sections={filterSections}
                  selected={filters}
                  onChange={handleFilterChange}
                />
              </div>
              <div className={styles.historyContent}>
                <div className={styles.columnHeaders}>
                  <span className={styles.colImage}>Image</span>
                  <div
                    className={`${styles.headerInfoCluster} ${styles.headerInfoClusterHistory}`}
                  >
                    <span className={styles.colTitle}>Title/Info</span>
                    <div className={styles.headerRightColumn}>
                      <span className={styles.colSku}>SKU</span>
                      <span className={styles.colType}>Type</span>
                      <span className={styles.colStatus}>Status</span>
                    </div>
                  </div>
                </div>
                <DeliverySectionHeader
                  label="Past Digital Delivery"
                  variant="past"
                />
                {historyFiltersNoResults ? (
                  <div className={styles.noResultsMessage}>
                    No results found.
                  </div>
                ) : (
                  paginatedHistoryItems.map((item) => {
                    const isEditLoading = editInProgressId === item.mediaItemId;
                    const isDuplicateLoading =
                      String(duplicatingItemId) === String(item.mediaItemId);
                    const isArchiveLoading =
                      archivingItemId === item.mediaItemId;
                    const isItemLoading =
                      isEditLoading || isDuplicateLoading || isArchiveLoading;

                    const audioType = item.mediaTypeCode
                      ? item.mediaTypeCode.charAt(0).toUpperCase() +
                        item.mediaTypeCode.slice(1)
                      : "Audio";

                    return (
                      <AudioListItem
                        key={item.mediaItemId}
                        id={item.mediaItemId}
                        thumbnail={item.thumbnailPath}
                        title={item.title}
                        date={item.date}
                        speaker={item.contributorName}
                        episode={item.episode}
                        sku={item.sku}
                        type={audioType}
                        status={item.displayStatus || "In Review"}
                        isDraft={item.isDraft}
                        tags={item.tags || []}
                        pinPermissions={item.pinPermissions}
                        membershipPermissions={item.membershipPermissions}
                        isHistoryView
                        onDuplicate={() => handleDuplicate(item.mediaItemId)}
                        onArchive={() => handleArchiveToggle(item.mediaItemId)}
                        onEdit={() => handleEdit(item.mediaItemId)}
                        disabled={isItemLoading}
                        isEditLoading={isEditLoading}
                        isDuplicateLoading={isDuplicateLoading}
                        isArchiveLoading={isArchiveLoading}
                      />
                    );
                  })
                )}

                {historyTotalPages > 1 && (
                  <div className={styles.paginationWrapper}>
                    <Pagination
                      page={historyPage}
                      totalPages={historyTotalPages}
                      onPageChange={setHistoryPage}
                      ariaLabel="History pagination"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

DigitalDelivery.propTypes = {
  onAddNew: PropTypes.func,
  onEdit: PropTypes.func,
  currentItems: PropTypes.array,
  upcomingItems: PropTypes.array,
  historyItems: PropTypes.array,
  disableInitialFetch: PropTypes.bool,
};

DigitalDelivery.defaultProps = {
  onAddNew: () => {},
  onEdit: () => {},
  currentItems: [],
  upcomingItems: [],
  historyItems: [],
  disableInitialFetch: false,
};

export default DigitalDelivery;
